import { createFileRoute, redirect, retainSearchParams } from "@tanstack/react-router";
import { useState } from "react";
import { ConfigCodeView } from "../features/customization/components/config-code-overlay";
import type { WidgetConfig } from "../features/customization/lib/config-schema";
import { normalizeFeatures } from "../features/customization/lib/config-schema";
import { DevTools } from "../shared/components/dev-tools";
import { Shell } from "../shared/components/shell";
import { ViewToggle } from "../shared/components/view-toggle";
import { Widget } from "../shared/components/widget";
import { queryClient } from "../shared/lib/query-client";
import { useDevToolsEnabled } from "../shared/utils/devtools-override";
import { useEmbedMode } from "../shared/utils/embed-detection";
import { getDefaultTab, TAB_TO_ROUTE, type TabName } from "../shared/utils/navigation-utils";

export const Route = createFileRoute("/_layout")({
  component: LayoutComponent,
  search: {
    middlewares: [retainSearchParams(true)],
  },
  beforeLoad: ({ location }) => {
    // Get config from query cache (should already be loaded by WidgetConfigProvider)
    const config = queryClient.getQueryData<WidgetConfig>(["config"]);

    if (!config) {
      // Config not loaded yet, let the route continue (will be handled by provider)
      return;
    }

    // Normalize features to get boolean values
    const features = normalizeFeatures(config.features);

    // Determine which feature the current route belongs to
    const getFeatureFromPath = (pathname: string): TabName | null => {
      if (pathname === "/" || pathname.startsWith("/portfolio")) return "Portfolio";
      if (pathname.startsWith("/staking")) return "Staking";
      if (pathname.startsWith("/defi")) return "DeFi";
      if (pathname.startsWith("/swap")) return "Swap";
      return null;
    };

    const currentTab = getFeatureFromPath(location.pathname);

    // If on root path, always redirect to first enabled feature
    if (location.pathname === "/") {
      const defaultTab = getDefaultTab(features);
      const redirectRoute = TAB_TO_ROUTE[defaultTab];
      throw redirect({ to: redirectRoute });
    }

    // If we're on a feature route and it's disabled, redirect to first enabled feature
    if (currentTab) {
      const featureKey = currentTab.toLowerCase() as keyof typeof features;

      if (!features[featureKey]) {
        const defaultTab = getDefaultTab(features);
        const redirectRoute = TAB_TO_ROUTE[defaultTab];
        throw redirect({ to: redirectRoute });
      }
    }
  },
});

// Widget-only mode enabled via VITE_WIDGET_ONLY build flag (must be exported in CI)
const isWidgetOnlyBuild = import.meta.env.VITE_WIDGET_ONLY === "true";

function LayoutComponent() {
  const [showCode, setShowCode] = useState(false);
  const embed = useEmbedMode();
  const devToolsEnabled = useDevToolsEnabled();

  const handleToggleCode = () => {
    setShowCode((prev) => !prev);
  };

  // Customer build with devtools enabled: show widget + devtools only
  if ((isWidgetOnlyBuild || embed) && devToolsEnabled) {
    return (
      <>
        <Widget />
        <DevTools />
      </>
    );
  }

  // Customer build without devtools: show widget only
  if (isWidgetOnlyBuild || embed) {
    return <Widget />;
  }

  // Full demo app: show everything with shell
  return (
    <>
      <Shell>
        {showCode ? <ConfigCodeView /> : <Widget />}
        <ViewToggle showCode={showCode} onToggleCode={handleToggleCode} />
      </Shell>
      <DevTools />
    </>
  );
}
